import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextStyle,
  ViewStyle,
} from 'react-native';
import {
  AllergensKey,
  DocketItemStatus,
  DocketStatus,
} from '@oolio-group/domain';
import { useSession } from '../../../hooks/useSession';
import {
  matchInSortedModifierStyles,
  sortActiveModifierStyles,
} from '../../../utils/matchModifierStyle';
import {
  modifierTextColorStyle,
  modifierBackgroundColorStyle,
} from '../../../common/modifierColorStyles';
import { useInvokeWithDelay } from '../../../hooks/useInvokeWithDelay';
import DocketDisplayItemIcon from './DocketDisplayItemIcon';
import Icon from '../../Icon/Icon';
import { useColors } from '../../../hooks/useColors';
import { getNotifierItemTextStyleKey } from '../../../utils/docketHelper/notifierRows';
import DocketItemStyles from '../DocketItem.styles';
import { ItemNameMaxLines } from '../../../common/constants';
import { formatQuantityString } from '../../../utils/general';
import { formatAllergenName } from '../../../utils/allergens';
import { useReactiveVar } from '@apollo/client/react/hooks';
import {
  allDayViewHighlightedItemsVar,
  bulkSelectVar,
  selectedDocketVar,
} from '../../../state/apolloVars';
import {
  DocketDisplayComboItem,
  DocketDisplayModifierItem,
} from '../../../common/types';

export interface DocketDisplayItemProps {
  id: string;
  status: DocketStatus;
  itemStatus: DocketItemStatus;
  name: string;
  note?: string;
  quantity: number;
  variant?: string;
  modifiers?: DocketDisplayModifierItem[];
  comboItems?: DocketDisplayComboItem[];
  onPressDocketItem: () => Promise<void>;
  seatName?: string;
  allergens?: AllergensKey[];
  allergenSeatName?: string;
  course?: {
    id?: string;
    name?: string;
    priority?: number;
    callInfo?: {
      isCalled: boolean;
      calledAt: number;
      calledBy: string;
      calledByName?: string;
    };
  };
  isCourseCallEnabled?: boolean;
  isOtherItem?: boolean;
}

const DocketDisplayItemComponent: React.FC<DocketDisplayItemProps> = ({
  id,
  status,
  itemStatus,
  name,
  note,
  variant,
  quantity,
  modifiers,
  comboItems,
  onPressDocketItem,
  seatName,
  allergens,
  allergenSeatName,
  course,
  isCourseCallEnabled = false,
  isOtherItem = false,
}) => {
  const isBulkMode = bulkSelectVar();
  const isSelected = Object.keys(selectedDocketVar())?.length > 0;
  const styles = DocketItemStyles();
  const { colors } = useColors();
  const { session } = useSession();
  // Sort the active modifier styles once per reference so the list isn't
  // rebuilt for every modifier on every re-render (this component re-renders
  // often via its reactive-var subscriptions).
  const sortedModifierStyles = useMemo(
    () => sortActiveModifierStyles(session?.kitchenDisplay?.modifierStyles),
    [session?.kitchenDisplay?.modifierStyles],
  );
  const { loading: itemActionLoading, invokeFunction: invokeItemAction } =
    useInvokeWithDelay({ callBack: onPressDocketItem });

  const highlightedItems = useReactiveVar(allDayViewHighlightedItemsVar);
  const isHighlighted =
    status === DocketStatus.CREATED &&
    (itemStatus === DocketItemStatus.CREATED ||
      itemStatus === DocketItemStatus.RECALLED) &&
    highlightedItems.includes(id);

  // Check if course is on hold (waiting for call)
  const isCourseOnHold = useMemo(() => {
    return (
      isCourseCallEnabled && course && course.id && !course.callInfo?.isCalled
    );
  }, [isCourseCallEnabled, course]);

  const getTextStyle = useMemo(() => {
    const nameStyle = isHighlighted ? styles.nameHighlighted : styles.name;
    const textStyle: TextStyle[] = [nameStyle];

    if (isOtherItem) {
      const key = getNotifierItemTextStyleKey(itemStatus, status);
      if (key) textStyle.push(styles[key]);
      return textStyle;
    }

    switch (itemStatus) {
      case DocketItemStatus.TRANSFERRED:
        textStyle.push(styles.transferredItem);
        break;
      case DocketItemStatus.VOIDED:
        textStyle.push(styles.voidedItem);
        break;
      case DocketItemStatus.BUMPED:
        if (status == DocketStatus.CREATED) {
          textStyle.push(styles.preppedItem); // Prepped
        }
        break;
      case DocketItemStatus.REBUMPED:
        if (status == DocketStatus.CREATED) {
          textStyle.push(styles.completedItem); // Completed
        }
        break;
      case DocketItemStatus.SERVED:
        if (status == DocketStatus.CREATED) {
          textStyle.push(styles.servedItem); // Served
        }
        break;
      default:
        break;
    }

    return textStyle;
  }, [isHighlighted, isOtherItem, itemStatus, status, styles]);

  const getItemStyle = useMemo(() => {
    const itemStyle: ViewStyle[] = [styles.product];

    if (isHighlighted) {
      itemStyle.push(styles.productHighlighted);
    }

    // Add blur effect for uncalled courses
    if (isCourseOnHold) {
      itemStyle.push({ opacity: 0.6 });
    }

    return itemStyle;
  }, [isHighlighted, styles, isCourseOnHold]);

  const statusesToBlurDocketItem = [
    DocketItemStatus.SERVED,
    DocketItemStatus.REBUMPED,
  ];
  if (!session?.rebumpToggle)
    statusesToBlurDocketItem.push(DocketItemStatus.BUMPED);

  const shouldShowLoading =
    itemStatus == DocketItemStatus.CREATED ||
    itemStatus == DocketItemStatus.RECALLED ||
    itemStatus == DocketItemStatus.BUMPED ||
    itemStatus == DocketItemStatus.SERVED ||
    DocketItemStatus.REBUMPED;

  const renderModifier = (modifiers?: DocketDisplayModifierItem[]) => {
    return modifiers?.length
      ? modifiers.map((modifier, i) => {
          // A configured modifier style only recolours/italicises normal
          // modifier text — not highlighted or removed-default modifiers,
          // which already carry their own meaning.
          const matchedStyle =
            !isHighlighted && !modifier.removeDefault
              ? matchInSortedModifierStyles(modifier.name, sortedModifierStyles)
              : undefined;

          return (
            <View key={name + modifier.name + i} style={styles.line}>
              <Text
                style={[
                  styles.modifierBase,
                  styles.modifierSpacing,
                  modifier.removeDefault && styles.removedModifier,
                ]}
              >
                ↳{' '}
              </Text>
              <Text
                style={[
                  styles.modifierBase,
                  // Matched-style entries come first so the highlight and
                  // removed-default treatments below always take precedence.
                  matchedStyle?.color && styles.modifierStyled,
                  matchedStyle?.italic && styles.modifierItalic,
                  matchedStyle?.background && styles.modifierBackgroundPill,
                  modifierTextColorStyle(matchedStyle?.color),
                  matchedStyle?.background &&
                    modifierBackgroundColorStyle(matchedStyle?.color),
                  isHighlighted && styles.modifierHighlighted,
                  modifier.removeDefault && styles.removeDefaultModifier,
                ]}
              >
                {`${modifier.name}`}
              </Text>
            </View>
          );
        })
      : null;
  };

  const renderComboItems = (comboItems?: DocketDisplayComboItem[]) => {
    if (!comboItems?.length) return null;
    return comboItems.map((comboItem, index) => (
      <React.Fragment key={`combo-item-${index}`}>
        <View style={styles.line}>
          <Text numberOfLines={2} style={[styles.comboItem, ...getTextStyle]}>
            {comboItem.name}
          </Text>
        </View>
        {!!comboItem.notes ? (
          <View style={styles.line}>
            <Text
              numberOfLines={3}
              style={styles.note}
            >{`▶ ${comboItem.notes}`}</Text>
          </View>
        ) : null}

        {renderModifier(comboItem.modifiers)}
      </React.Fragment>
    ));
  };

  return (
    <TouchableOpacity
      testID="item"
      onPress={invokeItemAction}
      style={getItemStyle}
      disabled={isOtherItem || isBulkMode || isSelected}
    >
      <View style={styles.line}>
        <View style={[styles.qty, isHighlighted && styles.qtyHighlighted]}>
          <Text style={[styles.qtyText, getTextStyle]}>
            {formatQuantityString(quantity)}
          </Text>
        </View>
        <Text numberOfLines={ItemNameMaxLines} style={getTextStyle}>
          {name}
        </Text>
        <View style={styles.rightContainer}>
          {seatName && <Text style={styles.seat}>{seatName}</Text>}
          <DocketDisplayItemIcon
            status={status}
            itemStatus={itemStatus}
            isOtherItem={isOtherItem}
            bumpItemLoading={shouldShowLoading && itemActionLoading}
          />
        </View>
      </View>
      <View>
        {!!note ? (
          <View style={styles.line}>
            <Text numberOfLines={3} style={styles.note}>{`▶ ${note}`}</Text>
          </View>
        ) : null}
        {!!variant ? (
          <View style={styles.line}>
            <Text
              style={[
                styles.modifierBase,
                styles.modifierSpacing,
                isHighlighted && styles.modifierHighlighted,
              ]}
            >
              {variant}
            </Text>
          </View>
        ) : null}
        {renderComboItems(comboItems)}
        {renderModifier(modifiers)}
        {allergens?.length
          ? allergens.map((code, index) => (
              <View
                key={`${id}-allergen-${index}`}
                style={styles.allergenItemLabel}
                testID="allergen-item-label"
              >
                <Icon
                  size={20}
                  name="exclamation-triangle"
                  color={colors.allergenItemText}
                />
                <Text style={styles.allergenItemLabelText}>
                  {formatAllergenName(code)}
                </Text>
                {allergenSeatName ? (
                  <Text style={styles.allergenItemLabelSeat}>
                    {allergenSeatName}
                  </Text>
                ) : null}
                <View style={styles.allergenItemIconSpacer} />
              </View>
            ))
          : null}
      </View>
    </TouchableOpacity>
  );
};

export default DocketDisplayItemComponent;
